import store from 'app/store';
import {
  AttributeType,
  ExistingSystemType,
  LevelType,
  SidebarSectionType
} from 'data/types';
import { StructureStepRef } from 'utils/completed-steps-helpers';
import { getPlacedExistingEquipmentComponents } from 'utils/existing-equipment';
import { isNotEmpty } from 'utils/helpers';
import { generateStructureAccuracyData } from 'utils/ui';

export type StructureImportCompletionContext = {
  structure: LevelType[];
  projectSettings: AttributeType[];
  existingSystems: ExistingSystemType[];
};

function sectionDataRequirementsMet(
  section: SidebarSectionType,
  context: StructureImportCompletionContext
): boolean {
  if (section.id === 'orientation') {
    return isNotEmpty(
      context.projectSettings.find((ps) => ps.code === 'structure_orientation')
        ?.value
    );
  }
  if (section.id === 'space_details' || section.id === 'sizing_tuning') {
    return true;
  }
  if (section.id === 'electrical_panel') {
    return context.structure.some(
      (level) => level.electricalPanels && level.electricalPanels.length > 0
    );
  }
  if (section.id === 'existing_equipment') {
    return (
      getPlacedExistingEquipmentComponents(context.existingSystems).length > 0
    );
  }
  return section.requirementsMet === true;
}

export function getFullyCompletedStructureStepRefs(
  sections: SidebarSectionType[],
  context: StructureImportCompletionContext
): StructureStepRef[] | null {
  if (sections.length === 0) {
    return null;
  }
  const fullyFilledOut = sections.every((section) =>
    sectionDataRequirementsMet(section, context)
  );
  if (!fullyFilledOut) {
    return null;
  }

  const subStepRefs: StructureStepRef[] = [];
  const stepCodes: string[] = [];
  sections.forEach((section) => {
    if (section.step_code) {
      subStepRefs.push({
        stepCode: section.step_code,
        subStepCode: section.id
      });
      if (!stepCodes.includes(section.step_code)) {
        stepCodes.push(section.step_code);
      }
    }
  });

  return [
    ...subStepRefs,
    ...stepCodes.map((stepCode) => ({ stepCode, subStepCode: null }))
  ];
}

export function getStructureImportCompletionSteps(): StructureStepRef[] | null {
  const { structure, existingSystems } = store.getState().structure;
  const { projectSettings } = store.getState().project;

  return getFullyCompletedStructureStepRefs(
    generateStructureAccuracyData().sections,
    { structure, projectSettings, existingSystems }
  );
}
